import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import { Box, Typography, TextField } from '@mui/material'

import { Button } from '../../../interaxDesignSystemComponents'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../constants/colors'
import FunnelGraphVisualization from '../../feedbackGenerator/components/visualizations/FunnelGraphVisualization'
import type { LoopCommunication } from '../../feedbackGenerator/components/visualizations/StackedLineChartCenteredVisualization'
import { transformLoopsToFunnelData } from '../../feedbackGenerator/components/visualizations/funnelGraphDataTransformer'

/**
 * Real-world data with percentage values
 * talkedTotal values represent percentages, not milliseconds
 */
const REAL_DATA: LoopCommunication[] = [
  {
    loopIndex: 1,
    players: [
      { playerId: 'QO0Emas1XWfKS9xLOExQ3eL0mUG2', displayName: 'Tanja', talkedTotal: 20 },
      { playerId: 'T4tEF1vk65QtMpwOqcgaEPsq5bM2', displayName: 'Alfons SG', talkedTotal: 35 },
      { playerId: 'XGz9nPatIdPxL3ULwUS6ed8m0sC2', displayName: 'erik@intraction', talkedTotal: 22 },
      { playerId: 'xx4DvlbgavM4p0IUp78iWgcRQff2', displayName: 'Emma', talkedTotal: 12 }
    ]
  },
  {
    loopIndex: 2,
    players: [
      { playerId: 'QO0Emas1XWfKS9xLOExQ3eL0mUG2', displayName: 'Tanja', talkedTotal: 29 },
      { playerId: 'T4tEF1vk65QtMpwOqcgaEPsq5bM2', displayName: 'Alfons SG', talkedTotal: 26 },
      { playerId: 'XGz9nPatIdPxL3ULwUS6ed8m0sC2', displayName: 'erik@intraction', talkedTotal: 35 },
      { playerId: 'xx4DvlbgavM4p0IUp78iWgcRQff2', displayName: 'Emma', talkedTotal: 17 }
    ]
  }
]

const LOOP_LABELS = ['Loop 1', 'Loop 2']

/**
 * FunnelGraphDevTestV3
 *
 * Uses real-world session data where talkedTotal values are percentages.
 * This helps validate the funnel visualization with actual production data structure.
 */
export const FunnelGraphDevTestV3: React.FC = () => {
  const navigate = useNavigate()
  const [direction, setDirection] = useState<'horizontal' | 'vertical'>('horizontal')
  const [containerWidth, setContainerWidth] = useState<string>('')

  const loopSummaries = useMemo(
    () =>
      REAL_DATA.map((loop) => ({
        loopLabel: `Loop ${loop.loopIndex}`,
        total: loop.players.reduce((sum, player) => sum + player.talkedTotal, 0)
      })),
    []
  )

  // Transform data to see what's being sent to the funnel graph
  const transformedData = useMemo(() => {
    return transformLoopsToFunnelData(REAL_DATA, { loopLabels: LOOP_LABELS })
  }, [])

  const handleBack = () => {
    navigate(-1)
  }

  return (
    <Box sx={{ padding: '24px', backgroundColor: INTERAX_COLORS.offBlack, minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
        <Button variant="secondary" onClick={handleBack} icon={<ArrowLeftIcon className="w-4 h-4" />} />
        <Typography
          sx={{
            ...INTERAX_TYPOGRAPHY.headline1,
            color: INTERAX_COLORS.white
          }}
        >
          Funnel Graph Prototype · v3 (Percentage Data)
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: 1080 }}>
        <Typography sx={{ ...INTERAX_TYPOGRAPHY.body1, color: INTERAX_COLORS.thunderGrey }}>
          This version uses real-world session data where <strong>talkedTotal values are percentages</strong> (not
          milliseconds). Each loop bucket shows player participation percentages, allowing us to validate the funnel
          visualization with actual production data structure.
        </Typography>

        <Box
          sx={{
            padding: { xs: '16px', md: '24px' },
            backgroundColor: INTERAX_COLORS.heritageBlue40,
            borderRadius: '16px',
            border: `1px solid ${INTERAX_COLORS.heritageBlue}`,
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: '16px',
              justifyContent: 'space-between'
            }}
          >
            <Box>
              <Typography
                sx={{
                  ...INTERAX_TYPOGRAPHY.headline3,
                  color: INTERAX_COLORS.white
                }}
              >
                Real Session Data Preview
              </Typography>
              <Typography sx={{ ...INTERAX_TYPOGRAPHY.body2, color: INTERAX_COLORS.thunderGrey }}>
                Player participation percentages from actual session data. Values represent percentage of total talk
                time.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
              <Button
                variant={direction === 'horizontal' ? 'primary' : 'secondary'}
                onClick={() => setDirection('horizontal')}
              >
                Horizontal
              </Button>
              <Button
                variant={direction === 'vertical' ? 'primary' : 'secondary'}
                onClick={() => setDirection('vertical')}
              >
                Vertical
              </Button>
              <TextField
                label="Width"
                type="text"
                value={containerWidth}
                onChange={(e) => setContainerWidth(e.target.value)}
                placeholder="100% or 800px"
                size="small"
                sx={{
                  minWidth: 120,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(0,0,0,0.25)',
                    '& fieldset': {
                      borderColor: INTERAX_COLORS.heritageBlue
                    },
                    '&:hover fieldset': {
                      borderColor: INTERAX_COLORS.heritageBlue
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: INTERAX_COLORS.heritageBlue
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: INTERAX_COLORS.thunderGrey
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: INTERAX_COLORS.heritageBlue
                  },
                  '& .MuiInputBase-input': {
                    color: INTERAX_COLORS.white
                  }
                }}
              />
            </Box>
          </Box>

          <FunnelGraphVisualization
            loops={REAL_DATA}
            loopLabels={LOOP_LABELS}
            direction={direction}
            showLoopSummary
            height={direction === 'horizontal' ? 360 : 420}
          />
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: '16px'
          }}
        >
          <Box
            sx={{
              backgroundColor: INTERAX_COLORS.heritageBlue40,
              borderRadius: '16px',
              border: `1px solid ${INTERAX_COLORS.heritageBlue}`,
              padding: '16px'
            }}
          >
            <Typography
              sx={{
                ...INTERAX_TYPOGRAPHY.headline4,
                color: INTERAX_COLORS.white,
                marginBottom: '8px'
              }}
            >
              Loop Totals (Percentage)
            </Typography>
            <Typography sx={{ ...INTERAX_TYPOGRAPHY.body3, color: INTERAX_COLORS.thunderGrey, marginBottom: '12px' }}>
              Sum of player participation percentages per loop.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {loopSummaries.map((summary) => (
                <Box
                  key={summary.loopLabel}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(0,0,0,0.25)'
                  }}
                >
                  <Typography sx={{ ...INTERAX_TYPOGRAPHY.body2, color: INTERAX_COLORS.white }}>
                    {summary.loopLabel}
                  </Typography>
                  <Typography sx={{ ...INTERAX_TYPOGRAPHY.body2, color: INTERAX_COLORS.green }}>
                    {summary.total}%
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              backgroundColor: INTERAX_COLORS.heritageBlue40,
              borderRadius: '16px',
              border: `1px solid ${INTERAX_COLORS.heritageBlue}`,
              padding: '16px'
            }}
          >
            <Typography
              sx={{
                ...INTERAX_TYPOGRAPHY.headline4,
                color: INTERAX_COLORS.white,
                marginBottom: '8px'
              }}
            >
              Player Values Per Loop
            </Typography>
            <Typography sx={{ ...INTERAX_TYPOGRAPHY.body3, color: INTERAX_COLORS.thunderGrey, marginBottom: '12px' }}>
              Individual player percentages for each loop to verify all players are included.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {REAL_DATA.map((loop) => (
                <Box
                  key={loop.loopIndex}
                  sx={{
                    padding: '12px',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(0,0,0,0.25)'
                  }}
                >
                  <Typography sx={{ ...INTERAX_TYPOGRAPHY.body2, color: INTERAX_COLORS.white, marginBottom: '8px' }}>
                    Loop {loop.loopIndex} (Total: {loop.players.reduce((sum, p) => sum + p.talkedTotal, 0)}%)
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {loop.players.map((player) => (
                      <Box key={player.playerId} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ ...INTERAX_TYPOGRAPHY.body3, color: INTERAX_COLORS.thunderGrey }}>
                          {player.displayName}:
                        </Typography>
                        <Typography sx={{ ...INTERAX_TYPOGRAPHY.body3, color: INTERAX_COLORS.green }}>
                          {player.talkedTotal}%
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Data Preview Section */}
        <Box
          sx={{
            backgroundColor: INTERAX_COLORS.heritageBlue40,
            borderRadius: '16px',
            border: `1px solid ${INTERAX_COLORS.heritageBlue}`,
            padding: '16px'
          }}
        >
          <Typography
            sx={{
              ...INTERAX_TYPOGRAPHY.headline4,
              color: INTERAX_COLORS.white,
              marginBottom: '8px'
            }}
          >
            Transformed Data Preview (Sent to Funnel Graph Library)
          </Typography>
          <Typography sx={{ ...INTERAX_TYPOGRAPHY.body3, color: INTERAX_COLORS.thunderGrey, marginBottom: '12px' }}>
            This is the exact data structure being passed to the d3-funnel-graph library after transformation.
          </Typography>
          {transformedData ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                fontFamily: 'monospace',
                fontSize: '12px'
              }}
            >
              <Box>
                <Typography sx={{ color: INTERAX_COLORS.green, marginBottom: '4px', fontWeight: 600 }}>
                  Labels (Loop Names):
                </Typography>
                <Box
                  sx={{
                    padding: '8px',
                    backgroundColor: 'rgba(0,0,0,0.25)',
                    borderRadius: '4px',
                    color: INTERAX_COLORS.white
                  }}
                >
                  {JSON.stringify(transformedData.labels, null, 2)}
                </Box>
              </Box>

              <Box>
                <Typography sx={{ color: INTERAX_COLORS.green, marginBottom: '4px', fontWeight: 600 }}>
                  SubLabels (Player Names):
                </Typography>
                <Box
                  sx={{
                    padding: '8px',
                    backgroundColor: 'rgba(0,0,0,0.25)',
                    borderRadius: '4px',
                    color: INTERAX_COLORS.white
                  }}
                >
                  {JSON.stringify(transformedData.subLabels, null, 2)}
                </Box>
                <Typography sx={{ color: INTERAX_COLORS.thunderGrey, fontSize: '11px', marginTop: '4px' }}>
                  Total Players: {transformedData.subLabels.length}
                </Typography>
              </Box>

              <Box>
                <Typography sx={{ color: INTERAX_COLORS.green, marginBottom: '4px', fontWeight: 600 }}>
                  Values (Player percentages per loop):
                </Typography>
                <Box
                  sx={{
                    padding: '8px',
                    backgroundColor: 'rgba(0,0,0,0.25)',
                    borderRadius: '4px',
                    color: INTERAX_COLORS.white,
                    overflowX: 'auto'
                  }}
                >
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(transformedData.values, null, 2)}
                  </pre>
                </Box>
                <Typography sx={{ color: INTERAX_COLORS.thunderGrey, fontSize: '11px', marginTop: '4px' }}>
                  Format: [Loop1: [player1, player2, player3, player4], Loop2: [player1, player2, player3, player4]]
                </Typography>
              </Box>

              <Box>
                <Typography sx={{ color: INTERAX_COLORS.green, marginBottom: '4px', fontWeight: 600 }}>
                  Colors (Gradients per loop):
                </Typography>
                <Box
                  sx={{
                    padding: '8px',
                    backgroundColor: 'rgba(0,0,0,0.25)',
                    borderRadius: '4px',
                    color: INTERAX_COLORS.white
                  }}
                >
                  {JSON.stringify(transformedData.colors, null, 2)}
                </Box>
              </Box>

              <Box>
                <Typography sx={{ color: INTERAX_COLORS.green, marginBottom: '4px', fontWeight: 600 }}>
                  Complete Data Structure:
                </Typography>
                <Box
                  sx={{
                    padding: '8px',
                    backgroundColor: 'rgba(0,0,0,0.25)',
                    borderRadius: '4px',
                    color: INTERAX_COLORS.white,
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}
                >
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '11px' }}>
                    {JSON.stringify(transformedData, null, 2)}
                  </pre>
                </Box>
              </Box>
            </Box>
          ) : (
            <Typography sx={{ color: INTERAX_COLORS.redError }}>
              Error: Failed to transform data. Check console for details.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default FunnelGraphDevTestV3
